/**
 * PeopleRatesSection.jsx - Tracking > Setup > People & rates: everyone on the
 * job and their rates for THIS job (MASTER SUMMARY rows 37-60) and the
 * per-day expenses an Expense day adds (the E-sheet's row 25), saved together.
 *   Sell rate + OT Multi? (YES = OT x1.3, DT x1.7) - what the customer is billed.
 *   Cost rate + OT Multi? - 'cost-rates' area only: starts at the person's
 *     burden rate (Staff cost rates) until a rate for this job is typed.
 *   Per day: round-trip airfare (a Travel day takes half), hotel, car/taxi,
 *     meals (60 by default), misc, other.
 * A person with no days logged can be taken off the job.
 */
import { useEffect, useState } from 'react';
import { Btn, GREEN, MUTED, NumInput, Section, TextInput, td, th } from '../quotes/quoteUi';
import { savePeople } from '../costs/costApi';
import { EXPENSE_COLS, EXPENSE_LABELS, Empty, Hint } from './trackingUi';

const copy = (list) => list.map((p) => ({ ...p, perDay: { ...p.perDay } }));

export default function PeopleRatesSection({ data, projectId, editable, seesCosts, setData, onDirty }) {
  const [people, setPeople] = useState(() => copy(data.people));
  const [removed, setRemoved] = useState([]);
  useEffect(() => {
    setPeople(copy(data.people));
    setRemoved([]);
  }, [data.people]);
  const dirty = removed.length > 0 || JSON.stringify(people) !== JSON.stringify(data.people);
  useEffect(() => {
    if (onDirty) onDirty(dirty);
  }, [dirty, onDirty]);
  const [newName, setNewName] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const staffNames = (data.staffDirectory || []).map((s) => s.name);
  const hoursOf = (name) => {
    const p = (data.tracking.people || []).find((x) => x.name.toLowerCase() === name.toLowerCase());
    return p ? p.hours + p.expenseTotal : 0;
  };
  const set = (i, patch) => setPeople((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const add = () => {
    const n = newName.trim();
    if (!n) return;
    if (people.some((p) => p.name.toLowerCase() === n.toLowerCase())) {
      setMsg({ kind: 'error', text: `${n} is already on this job.` });
      return;
    }
    const st = (data.staffDirectory || []).find((s) => s.name.toLowerCase() === n.toLowerCase());
    setPeople((ps) => [
      ...ps,
      {
        name: st ? st.name : n,
        level: st ? st.skillLevel : '',
        sellRate: '',
        sellOt: true,
        perDay: { airfare: 0, hotel: 0, car: 0, meals: 60, misc: 0, other: 0 },
        costFromStaff: true,
        isNew: true,
      },
    ]);
    setNewName('');
    setMsg(null);
  };
  const save = async () => {
    setBusy(true);
    const r = await savePeople(
      projectId,
      people.map(({ isNew, ...p }) => p),
      removed
    );
    setBusy(false);
    if (!r.ok) {
      setMsg({ kind: 'error', text: (r.body && r.body.error) || 'Could not save the people and rates.' });
      return;
    }
    setMsg({ kind: 'ok', text: 'People and rates saved.' });
    setData(r.body);
  };

  return (
    <Section
      title={
        <>
          People &amp; rates{' '}
          <Hint
            title="People & rates"
            tip={
              seesCosts
                ? "The sell rate is what the customer is billed per hour; the cost rate is what the hour costs this job - it starts at the person's burden rate (Engineers > Staff cost rates) until you type another. OT Multi? YES = OT x1.3 and DT x1.7."
                : "The sell rate is what the customer is billed per hour (it starts at the customer's rate for the person's level). OT Multi? YES = OT x1.3 and DT x1.7."
            }
            from="the tracking sheet's MASTER SUMMARY rows 37-60 and each E-sheet's row 25"
          />
        </>
      }
      hint="Everyone who logs time on this job, what an hour sells for, and what one Expense day adds."
      right={
        editable && (
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextInput
              aria-label="New person"
              list="pr-staff"
              value={newName}
              onChange={setNewName}
              placeholder="Add a person..."
              style={{ width: 170 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add();
              }}
            />
            <datalist id="pr-staff">
              {staffNames.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <Btn small onClick={add}>
              Add
            </Btn>
            <Btn small primary disabled={!dirty || busy} onClick={save}>
              {busy ? 'Saving...' : 'Save people & rates'}
            </Btn>
          </span>
        )
      }
    >
      {msg && (
        <div
          role={msg.kind === 'error' ? 'alert' : 'status'}
          style={{ marginBottom: 8, fontSize: 12, fontWeight: 600, color: msg.kind === 'error' ? '#B71C1C' : GREEN }}
        >
          {msg.text}
        </div>
      )}
      {dirty && (
        <div role="status" style={{ marginBottom: 8, fontSize: 11.5, fontWeight: 700, color: '#B45309' }}>
          Unsaved changes - press Save people &amp; rates.
        </div>
      )}
      {!people.length ? (
        <Empty text="Nobody on this job yet. Add the people who will log time (type a name above)." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="People and rates" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th} rowSpan={2}>
                  Person
                </th>
                <th style={{ ...th, textAlign: 'center', borderBottom: 0 }} colSpan={seesCosts ? 4 : 2}>
                  Rates per hour
                </th>
                <th style={{ ...th, textAlign: 'center', borderBottom: 0, background: '#F6F8F3' }} colSpan={EXPENSE_COLS.length}>
                  An Expense day adds
                </th>
                <th style={th} rowSpan={2} />
              </tr>
              <tr>
                {['Sell', 'OT Multi?', ...(seesCosts ? ['Cost', 'OT Multi?'] : [])].map((x, i) => (
                  <th key={`${x}${i}`} style={th}>
                    {x}
                  </th>
                ))}
                {EXPENSE_COLS.map((k) => (
                  <th key={k} style={{ ...th, background: '#F6F8F3' }}>
                    {k === 'airfare' ? 'Airfare (round trip)' : EXPENSE_LABELS[k]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {people.map((p, i) => (
                <tr key={p.name}>
                  <td style={{ ...td, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {p.name}
                    <div style={{ fontSize: 10, color: MUTED, fontWeight: 500 }}>
                      {p.level || 'no level'}
                      {p.inStaff === false ? ' · not in the staff table' : ''}
                      {p.isNew ? ' · new' : ''}
                    </div>
                  </td>
                  <td style={{ ...td, width: 84 }}>
                    <NumInput
                      aria-label={`${p.name} sell rate`}
                      value={p.sellRate}
                      disabled={!editable}
                      onChange={(v) => set(i, { sellRate: v })}
                      style={Number(p.sellRate) > 0 ? undefined : { borderColor: '#EF9A9A' }}
                    />
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      aria-label={`${p.name} sell OT multi`}
                      checked={p.sellOt !== false}
                      disabled={!editable}
                      onChange={(e) => set(i, { sellOt: e.target.checked })}
                    />
                  </td>
                  {seesCosts && (
                    <td style={{ ...td, width: 96 }}>
                      <NumInput
                        aria-label={`${p.name} cost rate`}
                        value={p.costRate}
                        disabled={!editable}
                        onChange={(v) => set(i, { costRate: v, costFromStaff: v === '' })}
                        style={
                          p.costKnown === false && p.costFromStaff !== false && !(Number(p.costRate) > 0)
                            ? { borderColor: '#EF9A9A' }
                            : undefined
                        }
                      />
                      <div style={{ fontSize: 9, color: MUTED }}>
                        {p.costFromStaff ? (p.costKnown ? 'burden rate' : 'none - type one') : 'this job'}
                      </div>
                    </td>
                  )}
                  {seesCosts && (
                    <td style={{ ...td, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        aria-label={`${p.name} cost OT multi`}
                        checked={p.costOt !== false}
                        disabled={!editable}
                        onChange={(e) => set(i, { costOt: e.target.checked })}
                      />
                    </td>
                  )}
                  {EXPENSE_COLS.map((k) => (
                    <td key={k} style={{ ...td, width: 80, background: '#FCFDFB' }}>
                      <NumInput
                        aria-label={`${p.name} ${EXPENSE_LABELS[k]}`}
                        value={p.perDay[k]}
                        disabled={!editable}
                        onChange={(v) => set(i, { perDay: { ...p.perDay, [k]: v } })}
                      />
                    </td>
                  ))}
                  <td style={{ ...td, width: 30 }}>
                    {editable &&
                      (hoursOf(p.name) > 0 ? (
                        <span
                          title="Has days logged - remove their days first"
                          style={{ fontSize: 10, color: MUTED, whiteSpace: 'nowrap' }}
                        >
                          in use
                        </span>
                      ) : (
                        <Btn
                          small
                          danger
                          aria-label={`Remove ${p.name}`}
                          onClick={() => {
                            setPeople((ps) => ps.filter((_, j) => j !== i));
                            if (!p.isNew) setRemoved((r) => [...r, p.name]);
                          }}
                        >
                          x
                        </Btn>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
